/**
 * The single typed firing idiom for CLIENT-fired Heap events.
 *
 * This is the ONLY place client code declares an event. Call sites import a
 * typed `track<Event>()` helper; they never touch `window.heap` and never pass
 * a raw domain object. A forbidden prop (e.g. `offer_price`) is a **compile
 * error** via the `EventProps` mapped type, and is ALSO dropped at runtime by
 * the per-event allow-list (`EVENT_SCHEMAS[*].props`) — two-way enforcement.
 *
 * Server-fired events (Offer Accepted/Rejected/Countered/Withdrawn, Digest
 * Email Clicked, Information Request Responded) live in the API analytics
 * service, NOT here.
 *
 * Event names use the canonical catalog casing EXACTLY ("Object Verbed", Title
 * Case). Never rename an event — that orphans Redshift columns; mint a new name.
 * Props are PII-safe: ids, enums, counts, booleans, durations only. No
 * addresses, prices, EMD, names, file names, or free text.
 */

import { trackEvent } from './heap'

/**
 * The canonical client-fired event names. Used as
 * the literal name passed to Heap; treated as a stable contract.
 */
export const CLIENT_EVENTS = {
  SIGN_IN_COMPLETED: 'Sign-In Completed',
  PROPERTY_SUBMITTED: 'Property Submitted',
  OFFER_SUBMITTED: 'Offer Submitted',
  INFORMATION_REQUEST_SENT: 'Information Request Sent',
  CONTRACT_UPLOADED: 'Contract Uploaded',
  BUYBOX_CREATED: 'Buybox Created',
  BUYBOX_EDITED: 'Buybox Edited',
  ONBOARDING_STEP_COMPLETED: 'Onboarding Step Completed',
} as const

/**
 * Per-event typed props. The keys here are the exhaustive PII-safe property set
 * for each client event. Adding a key that is not also listed in the
 * matching `EVENT_SCHEMAS[*].props` would be silently dropped at runtime, so the
 * two MUST be kept in lock-step (the test suite asserts this invariant).
 */
export interface ClientEventProps {
  'Sign-In Completed': { method: 'password' | 'google' | 'magic_link' }
  'Property Submitted': { property_id: string; submission_type: 'available' | 'draft' }
  'Offer Submitted': { offer_id: string; property_id: string }
  'Information Request Sent': { request_id: string; property_id: string }
  'Contract Uploaded': { offer_id: string; property_id?: string }
  'Buybox Created': { buybox_id: string; buybox_market_count: number }
  'Buybox Edited': { buybox_id: string; buybox_market_count: number }
  'Onboarding Step Completed': { step: 'profile' | 'buybox' | 'buybox_skipped' | 'terms' }
}

export type ClientEventName = keyof ClientEventProps

/**
 * Mapped type over every client event. Call sites can use the generic
 * {@link track} surface and get compile-time-checked props per event name.
 */
export type EventProps = {
  [E in ClientEventName]: ClientEventProps[E]
}

/**
 * Per-event runtime allow-list. Mirrors the `ClientEventProps` keys so a
 * forbidden prop is dropped even if a call site bypasses the type system. This
 * is the runtime half of the two-way allow-list enforcement.
 */
export const EVENT_SCHEMAS: { [E in ClientEventName]: { props: readonly string[] } } = {
  'Sign-In Completed': { props: ['method'] },
  'Property Submitted': { props: ['property_id', 'submission_type'] },
  'Offer Submitted': { props: ['offer_id', 'property_id'] },
  'Information Request Sent': { props: ['request_id', 'property_id'] },
  'Contract Uploaded': { props: ['offer_id', 'property_id'] },
  'Buybox Created': { props: ['buybox_id', 'buybox_market_count'] },
  'Buybox Edited': { props: ['buybox_id', 'buybox_market_count'] },
  'Onboarding Step Completed': { props: ['step'] },
}

/**
 * The single generic client firing idiom. Type-checked against the canonical
 * event catalog: `track('Offer Submitted', { offer_id, property_id })`. The
 * payload is allow-list-filtered (per-event keys) and base-prop-stamped inside
 * the transport before it can reach Heap.
 *
 * No-ops entirely when Heap is not configured / identify has not run (enforced
 * by the transport).
 */
export function track<E extends ClientEventName>(event: E, props: EventProps[E]): void {
  trackEvent(event, props as Record<string, unknown>, EVENT_SCHEMAS[event].props)
}

// -----------------------------------------------------------------------------
// Typed per-event helpers. Thin wrappers over `track()` so call sites get a
// named, discoverable surface and uniform usage. Adding an event = add a name
// above, its props + schema, and one helper here.
// -----------------------------------------------------------------------------

export function trackSignInCompleted(props: EventProps['Sign-In Completed']): void {
  track(CLIENT_EVENTS.SIGN_IN_COMPLETED, props)
}

export function trackPropertySubmitted(props: EventProps['Property Submitted']): void {
  track(CLIENT_EVENTS.PROPERTY_SUBMITTED, props)
}

export function trackOfferSubmitted(props: EventProps['Offer Submitted']): void {
  track(CLIENT_EVENTS.OFFER_SUBMITTED, props)
}

export function trackInformationRequestSent(props: EventProps['Information Request Sent']): void {
  track(CLIENT_EVENTS.INFORMATION_REQUEST_SENT, props)
}

export function trackContractUploaded(props: EventProps['Contract Uploaded']): void {
  track(CLIENT_EVENTS.CONTRACT_UPLOADED, props)
}

export function trackBuyboxCreated(props: EventProps['Buybox Created']): void {
  track(CLIENT_EVENTS.BUYBOX_CREATED, props)
}

export function trackBuyboxEdited(props: EventProps['Buybox Edited']): void {
  track(CLIENT_EVENTS.BUYBOX_EDITED, props)
}

export function trackOnboardingStepCompleted(props: EventProps['Onboarding Step Completed']): void {
  track(CLIENT_EVENTS.ONBOARDING_STEP_COMPLETED, props)
}
